import React, { useState, useEffect } from 'react';
import { 
  RefreshCw, 
  Play, 
  Trash2, 
  FileText, 
  Receipt, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertTriangle,
  DollarSign,
  Calendar,
  User,
  Building,
  Upload,
  Eye
} from 'lucide-react';
import ApiService from '../services/ApiService';
import ClientApprovalImport from './ClientApprovalImport';

const StagingArea = () => {
  const [stagingData, setStagingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [selectedBills, setSelectedBills] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCreatedItems, setShowCreatedItems] = useState(false);

  useEffect(() => {
    loadStagingArea();
  }, []);

  const loadStagingArea = async () => {
    setLoading(true);
    try {
      const data = await ApiService.apiCall('/staging/area');
      setStagingData(data);
    } catch (error) {
      console.error('Error loading staging area:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBatchProcess = async (processAll = false) => {
    setProcessing(true);
    try {
      const request = {
        processAllStaged: processAll,
        invoiceIds: processAll ? undefined : selectedInvoices,
        billIds: processAll ? undefined : selectedBills
      };

      const result = await ApiService.apiCall('/staging/batch-process', {
        method: 'POST',
        body: JSON.stringify(request)
      });

      alert(`Batch processing complete!\n\nInvoices: ${result.invoicesSuccessful}/${result.invoicesProcessed} successful\nBills: ${result.billsSuccessful}/${result.billsProcessed} successful`);
      
      setSelectedInvoices([]);
      setSelectedBills([]);
      loadStagingArea();
    } catch (error) {
      console.error('Error during batch processing:', error);
      alert('Batch processing failed: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleRetryFailed = async () => {
    setProcessing(true);
    try {
      const result = await ApiService.apiCall('/staging/retry-failed', {
        method: 'POST'
      });

      alert(`Retry complete!\n\nInvoices: ${result.invoicesSuccessful}/${result.invoicesProcessed} successful\nBills: ${result.billsSuccessful}/${result.billsProcessed} successful`);
      
      loadStagingArea();
    } catch (error) {
      console.error('Error retrying failed items:', error);
      alert('Retry failed: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleImportComplete = (result) => {
    setShowImportModal(false);
    loadStagingArea();
  };

  const handleSyncBillPayments = async () => {
    setProcessing(true);
    try {
      const result = await ApiService.apiCall('/billpayment/sync', {
        method: 'POST',
        body: JSON.stringify({})
      });

      const message = `Bill Payment Sync Complete!\n\n` +
        `Bills Checked: ${result.totalBillsChecked}\n` +
        `Bills Updated: ${result.billsUpdated}\n` +
        `New Payments Found: ${result.newPaymentsFound}\n` +
        `Errors: ${result.errorCount}`;

      if (result.errorCount > 0) {
        alert(message + `\n\nErrors:\n${result.errors.join('\n')}`);
      } else {
        alert(message);
      }
      
      loadStagingArea();
    } catch (error) {
      console.error('Error syncing bill payments:', error);
      alert('Bill payment sync failed: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteStaged = async (type, id) => {
    if (window.confirm(`Are you sure you want to remove this staged ${type}?`)) {
      try {
        await ApiService.apiCall(`/staging/${type}/${id}`, { method: 'DELETE' });
        loadStagingArea();
      } catch (error) {
        console.error(`Error deleting staged ${type}:`, error);
        alert(`Error deleting staged ${type}: ` + error.message);
      }
    }
  };

  const handleClearAllStaged = async (resetWorkOrders = false) => {
    const enhancedSummary = getEnhancedSummary();
    if (!enhancedSummary) return;

    const totalToDelete = enhancedSummary.invoices.staged + enhancedSummary.bills.staged;

    if (totalToDelete === 0) {
      alert('No staged items to clear.');
      return;
    }

    const confirmMessage = resetWorkOrders
      ? `⚠️ WARNING: This will permanently delete ALL STAGED items AND reset work orders!\n\n` +
        `Staged Invoices: ${enhancedSummary.invoices.staged}\n` +
        `Staged Bills: ${enhancedSummary.bills.staged}\n` +
        `Total items to delete: ${totalToDelete}\n\n` +
        `✅ Work orders will be reset to "Completed" status\n` +
        `✅ You will be able to re-finalize them\n` +
        `❌ Failed items will NOT be deleted (kept for review)\n\n` +
        `This action CANNOT be undone!\n\n` +
        `Are you absolutely sure you want to continue?`
      : `⚠️ WARNING: This will permanently delete ALL STAGED items!\n\n` +
        `Staged Invoices: ${enhancedSummary.invoices.staged}\n` +
        `Staged Bills: ${enhancedSummary.bills.staged}\n` +
        `Total items to delete: ${totalToDelete}\n\n` +
        `⚠️ Work orders will remain "Finalized"\n` +
        `⚠️ You will NOT be able to re-finalize them\n` +
        `❌ Failed items will NOT be deleted (kept for review)\n\n` +
        `This action CANNOT be undone!\n\n` +
        `Are you absolutely sure you want to continue?`;

    if (window.confirm(confirmMessage)) {
      setProcessing(true);
      try {
        const result = await ApiService.clearAllStaged(resetWorkOrders);
        const message = resetWorkOrders
          ? `✅ Successfully cleared staging area!\n\n` +
            `Invoices removed: ${result.invoicesCleared}\n` +
            `Bills removed: ${result.billsCleared}\n` +
            `Work orders reset: ${result.workOrdersReset}\n\n` +
            `Failed items kept for review.\n` +
            `You can now re-finalize these work orders.`
          : `✅ Successfully cleared staging area!\n\n` +
            `Invoices removed: ${result.invoicesCleared}\n` +
            `Bills removed: ${result.billsCleared}\n\n` +
            `Failed items kept for review.`;
        alert(message);
        loadStagingArea();
      } catch (error) {
        console.error('Error clearing staged items:', error);
        alert('❌ Error clearing staged items: ' + error.message);
      } finally {
        setProcessing(false);
      }
    }
  };


  const getStatusBadge = (status) => {
    const statusConfig = {
      'Staged': { color: 'bg-blue-100 text-blue-800', icon: Clock },
      'Processing': { color: 'bg-yellow-100 text-yellow-800', icon: RefreshCw },
      'Created': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'Failed': { color: 'bg-red-100 text-red-800', icon: XCircle }
    };

    const config = statusConfig[status] || statusConfig['Staged'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  // Filter items based on showCreatedItems toggle
  const getFilteredInvoices = () => {
    if (!stagingData?.stagedInvoices) return [];
    return showCreatedItems 
      ? stagingData.stagedInvoices 
      : stagingData.stagedInvoices.filter(invoice => invoice.status !== 'Created');
  };

  const getFilteredBills = () => {
    if (!stagingData?.stagedBills) return [];
    return showCreatedItems 
      ? stagingData.stagedBills 
      : stagingData.stagedBills.filter(bill => bill.status !== 'Created');
  };

  // Calculate enhanced summary statistics
  const getEnhancedSummary = () => {
    if (!stagingData) return null;
    
    const invoices = stagingData.stagedInvoices || [];
    const bills = stagingData.stagedBills || [];
    
    const invoiceStats = {
      staged: invoices.filter(i => i.status === 'Staged').length,
      processing: invoices.filter(i => i.status === 'Processing').length,
      created: invoices.filter(i => i.status === 'Created').length,
      failed: invoices.filter(i => i.status === 'Failed').length,
      totalAmount: invoices.filter(i => i.status !== 'Created').reduce((sum, i) => sum + i.amount, 0)
    };
    
    const billStats = {
      staged: bills.filter(b => b.status === 'Staged').length,
      processing: bills.filter(b => b.status === 'Processing').length,
      created: bills.filter(b => b.status === 'Created').length,
      failed: bills.filter(b => b.status === 'Failed').length,
      totalAmount: bills.filter(b => b.status !== 'Created').reduce((sum, b) => sum + b.amount, 0)
    };
    
    return {
      invoices: invoiceStats,
      bills: billStats,
      readyToProcess: invoiceStats.staged + billStats.staged,
      totalErrors: invoiceStats.failed + billStats.failed,
      totalCreated: invoiceStats.created + billStats.created
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Xero Staging Area</h1>
            <p className="text-gray-600 mt-1">Review and batch process invoices and bills before sending to Xero</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowCreatedItems(!showCreatedItems)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                showCreatedItems 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
            >
              <Eye className="w-4 h-4" />
              {showCreatedItems ? 'Hide Created' : 'Show Created'}
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              disabled={loading || processing}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              Import Client Approvals
            </button>
            <button
              onClick={() => handleSyncBillPayments()}
              disabled={loading || processing}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              <DollarSign className="w-4 h-4" />
              Sync Bill Payments
            </button>
            <button
              onClick={() => loadStagingArea()}
              disabled={loading || processing}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Enhanced Summary Cards */}
        {(() => {
          const enhancedSummary = getEnhancedSummary();
          if (!enhancedSummary) return null;
          
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg shadow border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Staged Invoices</p>
                    <p className="text-2xl font-bold text-blue-600">{enhancedSummary.invoices.staged}</p>
                    <p className="text-xs text-gray-500">${enhancedSummary.invoices.totalAmount.toFixed(2)}</p>
                  </div>
                  <FileText className="w-8 h-8 text-blue-500" />
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Staged Bills</p>
                    <p className="text-2xl font-bold text-red-600">{enhancedSummary.bills.staged}</p>
                    <p className="text-xs text-gray-500">${enhancedSummary.bills.totalAmount.toFixed(2)}</p>
                  </div>
                  <Receipt className="w-8 h-8 text-red-500" />
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Ready to Process</p>
                    <p className="text-2xl font-bold text-yellow-600">{enhancedSummary.readyToProcess}</p>
                    <p className="text-xs text-gray-500">Items staged</p>
                  </div>
                  <Clock className="w-8 h-8 text-yellow-500" />
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Processing</p>
                    <p className="text-2xl font-bold text-orange-600">{enhancedSummary.invoices.processing + enhancedSummary.bills.processing}</p>
                    <p className="text-xs text-gray-500">In progress</p>
                  </div>
                  <RefreshCw className="w-8 h-8 text-orange-500" />
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Successfully Created</p>
                    <p className="text-2xl font-bold text-green-600">{enhancedSummary.totalCreated}</p>
                    <p className="text-xs text-gray-500">{showCreatedItems ? 'Shown' : 'Hidden'}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Errors</p>
                    <p className="text-2xl font-bold text-red-600">{enhancedSummary.totalErrors}</p>
                    <p className="text-xs text-gray-500">Need attention</p>
                  </div>
                  <XCircle className="w-8 h-8 text-red-500" />
                </div>
              </div>
            </div>
          );
        })()}

        {/* Action Buttons */}
        {(() => {
          const enhancedSummary = getEnhancedSummary();
          if (!enhancedSummary) return null;
          
          return (
            <div className="flex gap-4 mb-6">
              <button
                onClick={() => handleBatchProcess(true)}
                disabled={processing || !enhancedSummary.readyToProcess}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
              >
                {processing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Process All Staged ({enhancedSummary.readyToProcess})
                  </>
                )}
              </button>
              
              {enhancedSummary.totalErrors > 0 && (
                <button
                  onClick={handleRetryFailed}
                  disabled={processing}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className="w-4 h-4" />
                  Retry Failed ({enhancedSummary.totalErrors})
                </button>
              )}

              {(enhancedSummary.readyToProcess > 0 || enhancedSummary.totalErrors > 0) && (
                <>
                  <button
                    onClick={() => handleClearAllStaged(false)}
                    disabled={processing}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
                    title="Clear staging but keep work orders finalized"
                  >
                    <Trash2 className="w-4 h-4" />
                    Clear Staged (Keep Finalized)
                  </button>
                  <button
                    onClick={() => handleClearAllStaged(true)}
                    disabled={processing}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
                    title="Clear staging AND reset work orders to Completed"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Clear & Reset to Completed
                  </button>
                </>
              )}
            </div>
          );
        })()}
      </div>

      {/* Staged Invoices */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          <div className="bg-blue-50 px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-blue-800 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Staged Invoices (Money Coming In)
            </h2>
          </div>
          
          {getFilteredInvoices().length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedInvoices(getFilteredInvoices().filter(i => i.status === 'Staged').map(i => i.id));
                          } else {
                            setSelectedInvoices([]);
                          }
                        }}
                        className="rounded"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getFilteredInvoices().map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {invoice.status === 'Staged' && (
                          <input
                            type="checkbox"
                            checked={selectedInvoices.includes(invoice.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedInvoices(prev => [...prev, invoice.id]);
                              } else {
                                setSelectedInvoices(prev => prev.filter(id => id !== invoice.id));
                              }
                            }}
                            className="rounded"
                          />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{invoice.invoiceNumber}</div>
                        <div className="text-sm text-gray-500">WO: {invoice.workOrderNumber}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Building className="w-4 h-4 text-gray-400 mr-2" />
                          <div className="text-sm text-gray-900">{invoice.contactName}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <DollarSign className="w-4 h-4 text-green-500 mr-1" />
                          ${invoice.amount.toFixed(2)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(invoice.dueDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(invoice.status)}
                        {invoice.errorMessage && (
                          <div className="text-xs text-red-600 mt-1 max-w-xs truncate" title={invoice.errorMessage}>
                            {invoice.errorMessage}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {(invoice.status === 'Staged' || invoice.status === 'Failed') && (
                          <button
                            onClick={() => handleDeleteStaged('invoice', invoice.id)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Remove from staging"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No staged invoices</p>
            </div>
          )}
        </div>
      </div>

      {/* Staged Bills */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          <div className="bg-red-50 px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-red-800 flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Staged Bills (Money Going Out)
            </h2>
          </div>
          
          {getFilteredBills().length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedBills(getFilteredBills().filter(b => b.status === 'Staged').map(b => b.id));
                          } else {
                            setSelectedBills([]);
                          }
                        }}
                        className="rounded"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bill #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subcontractor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getFilteredBills().map((bill) => (
                    <tr key={bill.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {bill.status === 'Staged' && (
                          <input
                            type="checkbox"
                            checked={selectedBills.includes(bill.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedBills(prev => [...prev, bill.id]);
                              } else {
                                setSelectedBills(prev => prev.filter(id => id !== bill.id));
                              }
                            }}
                            className="rounded"
                          />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{bill.billNumber}</div>
                        <div className="text-sm text-gray-500">WO: {bill.workOrderNumber}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <User className="w-4 h-4 text-gray-400 mr-2" />
                          <div className="text-sm text-gray-900">{bill.contactName}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <DollarSign className="w-4 h-4 text-red-500 mr-1" />
                          ${bill.amount.toFixed(2)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(bill.dueDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(bill.status)}
                        {bill.errorMessage && (
                          <div className="text-xs text-red-600 mt-1 max-w-xs truncate" title={bill.errorMessage}>
                            {bill.errorMessage}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {(bill.status === 'Staged' || bill.status === 'Failed') && (
                          <button
                            onClick={() => handleDeleteStaged('bill', bill.id)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Remove from staging"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <Receipt className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No staged bills</p>
            </div>
          )}
        </div>
      </div>

      {/* Selected Items Actions */}
      {(selectedInvoices.length > 0 || selectedBills.length > 0) && (
        <div className="bg-yellow-50 rounded-lg p-4 mb-6 border border-yellow-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <span className="font-medium text-yellow-800">
                Selected: {selectedInvoices.length} invoices, {selectedBills.length} bills
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setSelectedInvoices([]);
                  setSelectedBills([]);
                }}
                className="px-3 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 text-sm"
              >
                Clear Selection
              </button>
              <button
                onClick={() => handleBatchProcess(false)}
                disabled={processing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
              >
                Process Selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-gray-50 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-3">How Staging Works</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-blue-800 mb-2">📄 Invoices (Money In)</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Generated from finalized work orders</li>
              <li>• Sent to clients for payment</li>
              <li>• Created as ACCREC in Xero</li>
              <li>• Track money owed to you</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-red-800 mb-2">🧾 Bills (Money Out)</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Generated for each subcontractor</li>
              <li>• Track what you owe contractors</li>
              <li>• Created as ACCPAY in Xero</li>
              <li>• Manage contractor payments</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <ClientApprovalImport
          onClose={() => setShowImportModal(false)}
          onImportComplete={handleImportComplete}
        />
      )}
    </div>
  );
};

export default StagingArea;

